import React from "react";
import { Box, Stack, Typography, Container, Grid } from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";
import { BookOpen, ClipboardList, Target, ShieldCheck } from "lucide-react";
import PersonalReportPreview from "./PersonalReportPreview";

const BenefitsContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  backgroundColor: "#0D2847",
  padding: { xs: "50px 0", sm: "60px 0", md: "100px 0" },
  paddingTop: { xs: "60px", sm: "70px", md: "120px" },
  position: "relative",
  borderRadius: "20px 20px 0px 0px",
  overflow: "hidden",
  [theme.breakpoints.up("md")]: {
    borderRadius: "40px 40px 0px 0px",
  },
}));

// Modern Feature Card with better styling
const FeatureCard = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  gap: { xs: "10px", md: "16px" },
  padding: { xs: "16px", sm: "18px", md: "20px 24px" },
  borderRadius: "12px",
  background: "rgba(255, 255, 255, 0.03)",
  backdropFilter: "blur(10px)",
  border: "1px solid rgba(255, 255, 255, 0.1)",
  transition: "all 0.3s ease",
  "&:hover": {
    background: "rgba(255, 255, 255, 0.06)",
    transform: "translateY(-4px)",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
    borderColor: "rgba(255, 255, 255, 0.2)",
  },
  [theme.breakpoints.up("md")]: {
    padding: "20px 24px",
    minHeight: "90px",
    alignItems: "center",
  },
  [theme.breakpoints.down("md")]: {
    padding: "16px",
    flexDirection: "row",
  },
}));

// Icon Container with modern styling
const IconContainer = styled(Box)(({ theme }) => ({
  width: { xs: "45px", md: "49px" },
  height: { xs: "45px", md: "49px" },
  borderRadius: "10px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  background: "rgba(255, 255, 255, 0.08)",
  backdropFilter: "blur(10px)",
  border: "1px solid rgba(255, 255, 255, 0.15)",
}));

// PDF / Page-style mockup to replace mobile frame (auto height, compact)
const ReportPageMockup = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: { xs: 260, sm: 320, md: 360 },
  maxHeight: { xs: 360, md: 420 },
  borderRadius: "18px",
  background:
    "linear-gradient(145deg, rgba(255,255,255,0.98) 0%, rgba(245,248,255,0.98) 40%, rgba(230,238,255,0.98) 100%)",
  boxShadow:
    "0 18px 40px rgba(15,23,42,0.45), 0 8px 24px rgba(15,23,42,0.4), inset 0 0 0 1px rgba(148,163,184,0.3)",
  padding: { xs: 1.5, sm: 2 },
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(120deg, rgba(148,163,184,0.16), rgba(56,189,248,0.08), transparent 60%)",
    mixBlendMode: "multiply",
    opacity: 0.85,
    pointerEvents: "none",
  },
  [theme.breakpoints.up("md")]: {
    borderRadius: "22px",
  },
}));

const BenefitsSection = () => {
  const features = [
    {
      icon: <BookOpen size={22} color="#A1B0C1" />,
      title: "Improve Patient Experience",
      description: "Easy-to-read visual reports reduce confusion and anxiety associated with complex lab results",
      color: "#A1B0C1",
    },
    {
      icon: <ClipboardList size={22} color="#A7B4C2" />,
      title: "Enhance Diagnostic Efficiency",
      description: "Advisor user experience lean startup. Partnership A/B testing virality marketing release investor deployment traction customer.",
      color: "#A7B4C2",
    },
    {
      icon: <Target size={22} color="#A5A5AB" />,
      title: "Increase Patient Engagement",
      description: "Graphs, explanations, and alerts improve understanding and encourage proactive health behavior",
      color: "#A5A5AB",
    },
    {
      icon: <ShieldCheck size={22} color="#A7A7AC" />,
      title: "Boost Brand Trust & Retention",
      description: "Customized branding, AI insights, and multi-language access elevate your credibility",
      color: "#A7A7AC",
    },
  ];

  return (
    <BenefitsContainer>
      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 }, position: "relative", zIndex: 1, marginTop: "70px", paddingBottom: "70px" }}>
        {/* Heading Section */}
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          sx={{
            textAlign: "center",
            mb: { xs: 5, sm: 6, md: 8 },
            maxWidth: "900px",
            margin: "0 auto",
          }}
        >
          <Typography
            sx={{
              color: "#CACFD7",
              fontFamily: "Inter, sans-serif",
              fontSize: { xs: "28px", sm: "36px", md: "47.9px" },
              fontWeight: 700,
              mb: { xs: 2, md: 3 },
              lineHeight: { xs: "34px", md: "1.2" },
            }}
          >
            Smart Health Reports Help You
          </Typography>

          <Typography
            sx={{
              color: "#FFFFFF",
              fontFamily: "Inter, sans-serif",
              fontSize: { xs: "14px", sm: "16px", md: "18px" },
              fontWeight: 400,
              lineHeight: { xs: "22px", sm: "24px", md: "28px" },
              opacity: 0.9,
              px: { xs: 1, md: 0 },
            }}
          >
            Influencer startup interaction design. Paradigm shift research & development partner network iteration lean startup return on investment.
          </Typography>
        </Box>

        {/* Content Grid - Modern Layout */}
        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
          {/* Phone Mockup - Left Side on Desktop */}
          <Grid item xs={12} md={5}>
            <Box
              component={motion.div}
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                position: "relative",
              }}
            >
              <ReportPageMockup>
                <Box
                  sx={{
                    position: "relative",
                    zIndex: 1,
                    maxHeight: "100%",
                    overflowY: "auto",
                    overflowX: "hidden",
                    "&::-webkit-scrollbar": {
                      width: "6px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: "#e5e7eb",
                      borderRadius: "999px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "#94a3b8",
                      borderRadius: "999px",
                    },
                  }}
                >
                  <PersonalReportPreview />
                </Box>
              </ReportPageMockup>
            </Box>
          </Grid>

          {/* Feature Cards - Right Side on Desktop */}
          <Grid item xs={12} md={7}>
            <Stack
              spacing={{ xs: 2, sm: 2.5, md: 2.5 }}
              sx={{ width: "100%" }}
            >
              {features.map((feature, index) => (
                <FeatureCard
                  key={index}
                  component={motion.div}
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Box sx={{ display: "flex", gap: { xs: "14px", md: "18px" }, alignItems: "flex-start" }}>
                    <IconContainer>
                      {feature.icon}
                    </IconContainer>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          color: feature.color,
                          fontFamily: "Inter, sans-serif",
                          fontSize: { xs: "15px", sm: "16px", md: "17px" },
                          fontWeight: 700,
                          mb: { xs: 0.5, md: 1 },
                          lineHeight: { xs: "20px", md: "24px" },
                        }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography
                        sx={{
                          color: "#FFFFFF",
                          fontFamily: "Inter, sans-serif",
                          fontSize: { xs: "12px", sm: "13px", md: "14px" },
                          fontWeight: 400,
                          lineHeight: { xs: "18px", sm: "20px", md: "22px" },
                          opacity: 0.85,
                        }}
                      >
                        {feature.description}
                      </Typography>
                    </Box>
                  </Box>
                </FeatureCard>
              ))}
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </BenefitsContainer>
  );
};

export default BenefitsSection;
